import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CheckRegistryPage, CheckRegistryQuery } from '@fkwd-qc/shared';
import { getChecks, runAllChecks, runCheckForAllClients, toggleCheck } from '@/lib/api';
import { CHECKS_QUERY_KEY, OVERVIEW_QUERY_KEY } from '@/lib/query-keys';

export { CHECKS_QUERY_KEY } from '@/lib/query-keys';

/**
 * How often the registry is re-read while a roster-wide run is in progress, so its progress
 * line moves without anyone reloading.
 *
 * @since 1.5.0
 */
export const JOB_POLL_INTERVAL_MS = 5000;

/**
 * Reports whether any check in the loaded registry has a roster-wide run in progress.
 *
 * @since 1.5.0
 * @param pages - the registry pages loaded so far
 * @returns whether a job is active
 */
export function hasActiveJob(pages: CheckRegistryPage[] | undefined): boolean {
    return (pages ?? []).some((page) => page.rows.some((row) => row.activeJob !== null));
}

/**
 * Reads the check registry. The registry is a fixed list declared in the worker, so it
 * arrives whole; the infinite query shape is kept so the table reads like every other one.
 * While a roster-wide run is in progress it is re-read every few seconds.
 *
 * @since 1.0.0
 * @param query - the optional sort order
 * @returns the checks infinite query result
 */
export function useChecks(query: Partial<CheckRegistryQuery> = {}) {
    return useInfiniteQuery({
        queryKey: [...CHECKS_QUERY_KEY, query],
        queryFn: () => getChecks(query),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        refetchInterval: (state) => (hasActiveJob(state.state.data?.pages) ? JOB_POLL_INTERVAL_MS : false),
    });
}

/**
 * Switches one check on or off and refreshes the registry and the overview badges.
 *
 * @since 1.0.0
 * @returns the toggle mutation
 */
export function useToggleCheck() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ slug, isEnabled }: { slug: string; isEnabled: boolean }) => toggleCheck(slug, isEnabled),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: CHECKS_QUERY_KEY });
            await queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY });
        },
    });
}

/**
 * Queues every switched-on check against every client and refreshes the registry so the
 * progress lines appear.
 *
 * @since 1.0.0
 * @returns the run-all mutation
 */
export function useRunAllChecks() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => runAllChecks(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: CHECKS_QUERY_KEY });
            await queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY });
        },
    });
}

/**
 * Queues one check against every client and refreshes the registry so its progress line appears.
 *
 * @since 1.5.0
 * @returns the run-for-all mutation
 */
export function useRunCheckForAll() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (slug: string) => runCheckForAllClients(slug),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: CHECKS_QUERY_KEY });
        },
    });
}
